import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box, CircularProgress, Typography, IconButton, Tooltip, useMediaQuery,
} from '@mui/material';
import ArrowBackIcon       from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon    from '@mui/icons-material/ArrowForward';
import FullscreenIcon      from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon  from '@mui/icons-material/FullscreenExit';
import { getPitchByToken, loadBackgrounds, logView, updateView } from './services/pitchService';
import SlideRenderer    from './slides/SlideRenderer';
import ScaledIframe     from './ScaledIframe';
import LagbasLogo       from '../../components/LagbasLogo';
import { colors, fontFamily } from '../../theme/tikabTokens';
import { useQRLogo }    from '../../hooks/useQRLogo';
import { QRCodeSVG }    from 'qrcode.react';

export default function PublicPitchView() {
  const { shareToken }      = useParams();
  const [searchParams]      = useSearchParams();
  const isMobile            = useMediaQuery('(max-width:599px)');
  const [pitch,        setPitch]       = useState(null);
  const [backgrounds,  setBackgrounds] = useState([]);
  const [loading,      setLoading]     = useState(true);
  const [notFound,     setNotFound]    = useState(false);
  const [current,      setCurrent]     = useState(() => {
    const s = parseInt(searchParams.get('slide'), 10);
    return isNaN(s) ? 0 : s;
  });
  const [presentMode,  setPresentMode] = useState(false);
  const [showOverlay,  setShowOverlay] = useState(false);
  const hideTimer   = useRef(null);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const qrLogo      = useQRLogo();

  // Analytics
  const viewIdRef   = useRef(null);
  const maxSlideRef = useRef(0);

  const isApplabApp = pitch?.pitchType === 'applab_app';

  useEffect(() => {
    (async () => {
      try {
        const [p, bgs] = await Promise.all([
          getPitchByToken(shareToken),
          loadBackgrounds().catch(() => []),
        ]);
        if (!p) { setNotFound(true); }
        else {
          setPitch(p);
          if (!searchParams.get('slide')) setCurrent(p.currentSlide ?? 0);
        }
        setBackgrounds(bgs);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareToken]);

  // Extern bridge för AppLAB-appar — svarar med EXTERNAL_VIEW på alla anrop
  useEffect(() => {
    if (!isApplabApp) return;
    const SANDBOX = 'https://applab.lagbasn.se';
    function handleMsg(e) {
      if (e.origin !== SANDBOX) return;
      const { type, requestId, block } = e.data || {};
      if (type !== 'applab-request' || !requestId) return;
      const replyFn = (result, error) =>
        e.source?.postMessage({ type: 'applab-response', requestId, result: result ?? null, error: error ?? null }, SANDBOX);
      if (block === '_meta.getCurrentUser') return replyFn({ displayName: 'Gäst', email: null, photoURL: null, uid: null });
      if (block === '_meta.listBlocks')    return replyFn([{ name: 'ai.chat', status: 'available', available_in_external: false }]);
      replyFn(null, { code: 'EXTERNAL_VIEW', message: 'AI-funktioner är inte tillgängliga i delningar' });
    }
    window.addEventListener('message', handleMsg);
    return () => window.removeEventListener('message', handleMsg);
  }, [isApplabApp]);

  const visibleSlides = (pitch?.slides ?? []).filter(s => !s.hidden);
  const totalSlides = visibleSlides.length;

  // Statistikloggning — för AppLAB-appar loggar vi 1 "slide" och markerar alltid som klar
  useEffect(() => {
    if (!pitch?.id) return;
    if (isApplabApp) {
      logView(pitch.id, 1).then(id => { viewIdRef.current = id; }).catch(() => {});
      return () => {
        if (viewIdRef.current) updateView(pitch.id, viewIdRef.current, 0, 1).catch(() => {});
      };
    }
    if (!totalSlides) return;
    logView(pitch.id, totalSlides).then(id => { viewIdRef.current = id; }).catch(() => {});
    return () => {
      if (viewIdRef.current) {
        updateView(pitch.id, viewIdRef.current, maxSlideRef.current, totalSlides).catch(() => {});
      }
    };
  }, [pitch?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!pitch?.id || !viewIdRef.current) return;
    if (current > maxSlideRef.current) {
      maxSlideRef.current = current;
      updateView(pitch.id, viewIdRef.current, current, totalSlides).catch(() => {});
    }
  }, [current]); // eslint-disable-line react-hooks/exhaustive-deps

  const prev = useCallback(() => setCurrent(c => Math.max(0, c - 1)), []);
  const next = useCallback(() => setCurrent(c => Math.min(totalSlides - 1, c + 1)), [totalSlides]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') next();
      if (e.key === 'ArrowLeft')  prev();
      if (e.key === 'Escape')     exitPresentMode();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [next, prev]);

  useEffect(() => {
    const handler = () => { if (!document.fullscreenElement) setPresentMode(false); };
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  function enterPresentMode() {
    setPresentMode(true);
    document.documentElement.requestFullscreen?.();
  }

  function exitPresentMode() {
    setPresentMode(false);
    if (document.fullscreenElement) document.exitFullscreen?.();
  }

  function handleMouseMove() {
    if (!presentMode) return;
    setShowOverlay(true);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setShowOverlay(false), 2500);
  }

  // Touch swipe — horizontal swipe > 40px triggers navigation (only if not primarily vertical)
  function handleTouchStart(e) {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }
  function handleTouchEnd(e) {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;
    if (Math.abs(dx) < 40 || Math.abs(dy) > Math.abs(dx)) return;
    if (dx < 0) next();
    else prev();
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center',
        height: '100vh', bgcolor: '#000' }}>
        <CircularProgress sx={{ color: colors.orange }} />
      </Box>
    );
  }

  if (notFound) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100vh', bgcolor: '#111', gap: 2 }}>
        <Typography variant="h5" sx={{ color: '#eee', fontFamily }}>
          Presentationen hittades inte
        </Typography>
        <Typography sx={{ color: '#666', fontFamily }}>
          Länken kan ha upphört att gälla eller delning är inaktiverad.
        </Typography>
      </Box>
    );
  }

  const slide = visibleSlides[current] ?? null;
  const isExternal = pitch && (pitch.pitchType === 'html' || pitch.pitchType === 'pdf');

  // ── AppLAB-app — iframe i externt läge ───────────────────────────────────
  if (isApplabApp && pitch) {
    const appSrc = `https://applab.lagbasn.se/apps/${pitch.applab_ref?.app_id}/?mode=external`;
    return (
      <Box sx={{ height: '100dvh', bgcolor: '#0d0d0d', display: 'flex', flexDirection: 'column', fontFamily, color: 'white', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 },
          px: { xs: 1.5, sm: 2 }, py: { xs: 0.75, sm: 1 },
          bgcolor: '#1a1a1a', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
          <LagbasLogo />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ color: '#eee' }}>
              {pitch.title || 'Presentation'}
            </Typography>
            {pitch.tagline && (
              <Typography variant="caption" noWrap sx={{ color: '#777' }}>{pitch.tagline}</Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
          <iframe
            src={appSrc}
            title={pitch.title || 'App'}
            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            allow="clipboard-read; clipboard-write"
          />
        </Box>
      </Box>
    );
  }

  // ── Gemensam wrapper — fullskärm, alltid 100vh ────────────────────────────
  if (presentMode) {
    return (
      <Box
        onMouseMove={handleMouseMove}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        sx={{
          width: '100vw', height: '100vh', bgcolor: '#000',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative', overflow: 'hidden', cursor: showOverlay ? 'default' : 'none',
        }}
      >
        {isExternal ? (
          <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
            {pitch.pitchType === 'html' ? (
              <ScaledIframe
                src={pitch.externalFileUrl}
                title={pitch.title}
                qrUrl={pitch.shareToken ? `${window.location.origin}/p/${pitch.shareToken}` : undefined}
                qrLogo={qrLogo}
              />
            ) : (
              <>
                <iframe
                  src={pitch.externalFileUrl}
                  title={pitch.title}
                  style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                />
                {pitch.shareToken && !isMobile && (
                  <Box sx={{
                    position: 'absolute', bottom: 24, right: 24, zIndex: 10,
                    bgcolor: 'rgba(0,0,0,0.52)', borderRadius: '8px', p: '6px',
                    backdropFilter: 'blur(4px)', display: 'inline-flex',
                    pointerEvents: 'none',
                  }}>
                    <QRCodeSVG
                      value={`${window.location.origin}/p/${pitch.shareToken}`}
                      size={88}
                      level="H"
                      bgColor="transparent"
                      fgColor="rgba(255,255,255,0.90)"
                      imageSettings={qrLogo ? { src: qrLogo, width: 22, height: 22, excavate: true } : undefined}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        ) : (
          <Box sx={{ width: '100%', maxWidth: `${(100 * 16) / 9}vh` }}>
            {slide ? (
              <SlideRenderer
                interactive
                slide={slide}
                backgrounds={backgrounds}
                slideTheme={pitch?.slideTheme}
                altLogoUrl={pitch?.altLogoUrl}
                qrUrl={pitch?.slideTheme?.showQr
                  ? `${window.location.origin}/p/${shareToken}?slide=${current}`
                  : undefined}
                qrLogoUrl={pitch?.qrLogoUrl || undefined}
              />
            ) : (
              <Box sx={{ aspectRatio: '16/9', bgcolor: '#111' }} />
            )}
          </Box>
        )}

        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none',
          opacity: showOverlay ? 1 : 0, transition: 'opacity 0.3s' }}>
          {!isExternal && (<>
            <Box sx={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'auto' }}>
              <IconButton onClick={prev} disabled={current === 0}
                sx={{ bgcolor: 'rgba(0,0,0,0.5)', color: current === 0 ? '#333' : '#ccc',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.8)', color: 'white' } }}>
                <ArrowBackIcon />
              </IconButton>
            </Box>
            <Box sx={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'auto' }}>
              <IconButton onClick={next} disabled={current >= totalSlides - 1}
                sx={{ bgcolor: 'rgba(0,0,0,0.5)', color: current >= totalSlides - 1 ? '#333' : '#ccc',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.8)', color: 'white' } }}>
                <ArrowForwardIcon />
              </IconButton>
            </Box>
          </>)}
          <Box sx={{ position: 'absolute', top: 16, right: 16,
            display: 'flex', alignItems: 'center', gap: 1, pointerEvents: 'auto' }}>
            {!isExternal && (
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontFamily, fontSize: 13 }}>
                {current + 1} / {totalSlides}
              </Typography>
            )}
            <Tooltip title="Avsluta presentationsläge (Esc)">
              <IconButton size="small" onClick={exitPresentMode}
                sx={{ bgcolor: 'rgba(0,0,0,0.5)', color: '#aaa',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.8)', color: 'white' } }}>
                <FullscreenExitIcon />
              </IconButton>
            </Tooltip>
          </Box>
          {!isExternal && (
            <Box sx={{ position: 'absolute', bottom: 16, left: 0, right: 0,
              display: 'flex', justifyContent: 'center', gap: 0.5,
              flexWrap: 'wrap', pointerEvents: 'auto' }}>
              {visibleSlides.map((_, i) => (
                <Box key={i} onClick={() => setCurrent(i)} sx={{
                  width: i === current ? 20 : 6, height: 6, borderRadius: 3,
                  bgcolor: i === current ? colors.orange : 'rgba(255,255,255,0.25)',
                  cursor: 'pointer', transition: 'all 0.2s',
                  '&:hover': { bgcolor: i === current ? colors.orange : 'rgba(255,255,255,0.5)' },
                }} />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // ── Normalvy ──────────────────────────────────────────────────────────────
  return (
    <Box sx={{
      height: '100dvh', // dynamic viewport height — rätt på mobil med adressfält
      bgcolor: '#0d0d0d',
      display: 'flex', flexDirection: 'column',
      fontFamily, color: 'white', overflow: 'hidden',
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 },
        px: { xs: 1.5, sm: 2 }, py: { xs: 0.75, sm: 1 },
        bgcolor: '#1a1a1a',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        flexShrink: 0,
      }}>
        <LagbasLogo />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ color: '#eee' }}>
            {pitch.title || 'Presentation'}
          </Typography>
          {pitch.client && (
            <Typography variant="caption" noWrap sx={{ color: '#777' }}>{pitch.client}</Typography>
          )}
        </Box>
        {!isExternal && (
          <Typography variant="caption" sx={{ color: '#555', fontFamily, flexShrink: 0 }}>
            {current + 1} / {totalSlides}
          </Typography>
        )}
        <Tooltip title="Fullskärm">
          <IconButton size="small" sx={{ color: '#666', '&:hover': { color: '#ccc' }, flexShrink: 0 }}
            onClick={enterPresentMode}>
            <FullscreenIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Extern presentation (HTML/PDF) */}
      {isExternal ? (
        <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0, position: 'relative' }}>
          {pitch.pitchType === 'html' ? (
            <ScaledIframe
              src={pitch.externalFileUrl}
              title={pitch.title}
              qrUrl={pitch.shareToken ? `${window.location.origin}/p/${pitch.shareToken}` : undefined}
              qrLogo={qrLogo}
            />
          ) : (
            <>
              <iframe
                src={pitch.externalFileUrl}
                title={pitch.title}
                style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
              />
              {pitch.shareToken && !isMobile && (
                <Box sx={{
                  position: 'absolute', bottom: 24, right: 24, zIndex: 10,
                  bgcolor: 'rgba(0,0,0,0.52)', borderRadius: '8px', p: '6px',
                  backdropFilter: 'blur(4px)', display: 'inline-flex',
                  pointerEvents: 'none',
                }}>
                  <QRCodeSVG
                    value={`${window.location.origin}/p/${pitch.shareToken}`}
                    size={88}
                    level="H"
                    bgColor="transparent"
                    fgColor="rgba(255,255,255,0.90)"
                    imageSettings={qrLogo ? { src: qrLogo, width: 22, height: 22, excavate: true } : undefined}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      ) : (
        <>
          {/* Slide — edge-to-edge på mobil, centrerad med padding på desktop */}
          <Box
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            sx={{
              flex: 1, minHeight: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              p: { xs: 0, sm: 3 },
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ width: '100%', maxWidth: { xs: '100%', sm: 1100 }, height: { xs: '100%', sm: undefined } }}>
              {slide ? (
                <SlideRenderer
                  interactive
                  fitHeight
                  slide={slide}
                  backgrounds={backgrounds}
                  slideTheme={pitch?.slideTheme}
                  altLogoUrl={pitch?.altLogoUrl}
                  qrUrl={pitch?.slideTheme?.showQr
                    ? `${window.location.origin}/p/${shareToken}?slide=${current}`
                    : undefined}
                  qrLogoUrl={pitch?.qrLogoUrl || undefined}
                />
              ) : (
                <Box sx={{ aspectRatio: '16/9', bgcolor: '#222', borderRadius: 2,
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ color: '#555', fontFamily }}>Inga slides</Typography>
                </Box>
              )}
            </Box>

            {/* Navigeringsknappar — overlay på mobil, bredvid sliden på desktop */}
            <IconButton
              onClick={prev}
              disabled={current === 0}
              sx={{
                position: 'absolute',
                left: { xs: 8, sm: 16 },
                top: '50%', transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,0.45)',
                color: current === 0 ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.7)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)', color: 'white' },
                '&:disabled': { bgcolor: 'transparent' },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <IconButton
              onClick={next}
              disabled={current >= totalSlides - 1}
              sx={{
                position: 'absolute',
                right: { xs: 8, sm: 16 },
                top: '50%', transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,0.45)',
                color: current >= totalSlides - 1 ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.7)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)', color: 'white' },
                '&:disabled': { bgcolor: 'transparent' },
              }}
            >
              <ArrowForwardIcon />
            </IconButton>
          </Box>

          {/* Slide-dots */}
          <Box sx={{
            display: 'flex', justifyContent: 'center', gap: 0.5,
            py: { xs: 1, sm: 2 }, flexWrap: 'wrap', flexShrink: 0,
          }}>
            {visibleSlides.map((_, i) => (
              <Box
                key={i}
                onClick={() => setCurrent(i)}
                sx={{
                  width: i === current ? 20 : 6, height: 6, borderRadius: 3,
                  bgcolor: i === current ? colors.orange : 'rgba(255,255,255,0.2)',
                  cursor: 'pointer', transition: 'all 0.2s',
                  '&:hover': { bgcolor: i === current ? colors.orange : 'rgba(255,255,255,0.4)' },
                }}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}
